import { forwardRef } from 'react';
import Select, { ActionMeta, GroupBase, MultiValue, Props as ReactSelectProps, SelectInstance } from 'react-select';
import { combineClassNames } from '../../../Utils/ClassnameUtils';
import { GWPSearchSelectOption } from './GWPSearchSelect';

export interface GWPMultiSelectProps<T extends string | number>
	extends Omit<
		ReactSelectProps<GWPSearchSelectOption<T>, true, GroupBase<GWPSearchSelectOption<T>>>,
		'isMulti' | 'className' | 'classNamePrefix' | 'unstyled' | 'value' | 'defaultValue' | 'onChange'
	> {
	className?: string;
	id?: string;
	options: ReadonlyArray<GWPSearchSelectOption<T>>;
	value?: MultiValue<GWPSearchSelectOption<T>>;
	defaultValue?: MultiValue<GWPSearchSelectOption<T>>;
	size?: 'sm' | 'md' | 'lg';
	invalid?: boolean;
	onChange?: (
		newValue: MultiValue<GWPSearchSelectOption<T>>,
		actionMeta: ActionMeta<GWPSearchSelectOption<T>>,
	) => void;
	onValueChange?: (
		options: MultiValue<GWPSearchSelectOption<T>>,
		actionMeta: ActionMeta<GWPSearchSelectOption<T>>,
	) => void;
}

type GWPMultiSelectComponent = <T extends string | number>(
	props: GWPMultiSelectProps<T> &
		React.RefAttributes<SelectInstance<GWPSearchSelectOption<T>, true, GroupBase<GWPSearchSelectOption<T>>>>,
) => React.ReactElement | null;

const GWPMultiSelectInner = forwardRef(function GWPMultiSelectInner<T extends string | number>(
	props: GWPMultiSelectProps<T>,
	ref: React.ForwardedRef<SelectInstance<GWPSearchSelectOption<T>, true, GroupBase<GWPSearchSelectOption<T>>>>,
) {
	const {
		className,
		id,
		inputId,
		instanceId,
		size = 'md',
		invalid = false,
		isDisabled = false,
		menuPlacement = 'auto',
		closeMenuOnSelect = false,
		onChange,
		onValueChange,
		...rest
	} = props;

	const rootClassName = combineClassNames(
		'gwp-search-select',
		'gwp-multi-select',
		size,
		invalid ? 'invalid' : undefined,
		isDisabled ? 'disabled' : undefined,
		className,
	);

	const handleChange = (
		newValue: MultiValue<GWPSearchSelectOption<T>>,
		actionMeta: ActionMeta<GWPSearchSelectOption<T>>,
	) => {
		onChange?.(newValue, actionMeta);
		onValueChange?.(newValue, actionMeta);
	};

	return (
		<Select<GWPSearchSelectOption<T>, true, GroupBase<GWPSearchSelectOption<T>>>
			{...rest}
			ref={ref}
			inputId={inputId ?? id}
			instanceId={instanceId ?? inputId ?? id}
			className={rootClassName}
			classNamePrefix="gwp-search-select"
			isDisabled={isDisabled}
			isMulti
			closeMenuOnSelect={closeMenuOnSelect}
			menuPlacement={menuPlacement}
			onChange={handleChange}
			unstyled
		/>
	);
});

GWPMultiSelectInner.displayName = 'GWPMultiSelect';

const GWPMultiSelect = GWPMultiSelectInner as GWPMultiSelectComponent;

export default GWPMultiSelect;
